/* eslint-env es2020 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Table,
  Tag,
  Modal,
  InputNumber,
  message,
  Typography,
  Descriptions,
  Collapse,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { walletAPI, orderAPI, paypalAPI, stripeAPI } from '../api';
import { normalizeMoney } from '../utils/money';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const reasonLabelMap = {
  // 翻译后端 reason 枚举，便于表格展示
  deposit: 'Deposit',
  purchase: 'Purchase',
  refund: 'Refund',
  withdrawal: 'Withdrawal',
  adjustment: 'Adjustment',
  chargeback: 'Chargeback',
  transfer: 'Transfer',
};

const defaultPagination = {
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
};

const normalizeOrderRef = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
};

const MyWallet = () => {
  // Dropshipper 钱包页：余额概览、充值、流水详情
  const navigate = useNavigate();
  const location = useLocation();
  const [wallet, setWallet] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [pagination, setPagination] = useState({ ...defaultPagination });
  const [orderItemCounts, setOrderItemCounts] = useState({});

  const [paypalConfig, setPaypalConfig] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalScriptError, setPaypalScriptError] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState(null);
  const [draftTopUpAmount, setDraftTopUpAmount] = useState(null);
  const [paypalProcessing, setPaypalProcessing] = useState(false);
  const paypalButtonsRef = useRef(null);
  const [topUpModalVisible, setTopUpModalVisible] = useState(false);
  const [topUpStage, setTopUpStage] = useState('input');

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const quickTopUpAmounts = [10, 50, 100, 200, 500, 800, 1000];

  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripeProcessing, setStripeProcessing] = useState(false);

  // Payment context from order page
  const [paymentContext, setPaymentContext] = useState(null);

  const fetchWalletSummary = useCallback(async () => {
    // 获取当前用户钱包余额信息
    try {
      setSummaryLoading(true);
      const response = await walletAPI.getMyWallet();
      setWallet(response.data);
    } catch (error) {
      
      message.error('Failed to load wallet info');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchOrderSummaries = useCallback(async (txList) => {
    // 预取关联订单，便于在流水中展示商品数量；优先使用 order_id（后端返回的 order_number）
    const uniqueOrderNumbers = Array.from(
      new Set(
        txList
          .map((tx) => normalizeOrderRef(tx?.order_id))
          .filter((ref) => ref)
      )
    );

    if (uniqueOrderNumbers.length === 0) {
      setOrderItemCounts({});
      return;
    }

    const results = await Promise.all(uniqueOrderNumbers.map(async (orderNumber) => {
      try {
        const orderResponse = await orderAPI.getByNumber(orderNumber);
        const orderData = orderResponse.data;
        const itemCount = (orderData.items_detail || []).reduce((total, item) => total + (Number(item.quantity) || 0), 0);
        return { orderNumber, itemCount };
      } catch (error) {
        
        return { orderNumber, itemCount: null };
      }
    }));

    const mapping = {};
    results.forEach(({ orderNumber, itemCount }) => {
      mapping[orderNumber] = itemCount;
    });
    setOrderItemCounts(mapping);
  }, []);

  const fetchTransactions = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
    // 获取钱包流水列表，支持分页
    try {
      setTxLoading(true);
      const response = await walletAPI.getMyTransactions({ page, page_size: pageSize });
      const payload = response.data;
      const txData = payload.data || [];
      setTransactions(txData);
      setPagination(prev => ({
        ...prev,
        current: payload.page,
        pageSize: payload.page_size,
        total: payload.total,
      }));
      await fetchOrderSummaries(txData);
    } catch (error) {
      
      message.error('Failed to load wallet transactions');
    } finally {
      setTxLoading(false);
    }
  }, [fetchOrderSummaries, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchWalletSummary();
    fetchTransactions(1, pagination.pageSize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Check for payment context from navigation
    if (location.state?.paymentContext) {
      const ctx = location.state.paymentContext;
      setPaymentContext(ctx);
      // Auto-open top-up modal with pre-filled amount
      setTopUpAmount(ctx.amount);
      setDraftTopUpAmount(ctx.amount);
      setTopUpStage('confirm');
      setTopUpModalVisible(true);
      // Clear location state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadPayPalConfig = async () => {
      try {
        const response = await paypalAPI.getConfig();
        if (cancelled) return;
        setPaypalConfig(response.data);
        setPaypalScriptError(null);
      } catch (error) {
        
        setPaypalScriptError('Unable to load PayPal configuration');
        message.error('Unable to load PayPal configuration');
      }
    };

    loadPayPalConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkStripeAvailability = async () => {
      try {
        await stripeAPI.getConfig();
        if (cancelled) return;
        setStripeEnabled(true);
      } catch (error) {
        
        // Stripe is optional, so just mark it as disabled
        setStripeEnabled(false);
      }
    };

    checkStripeAvailability();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!paypalConfig?.client_id) {
      return;
    }

    if (window.paypal) {
      setPaypalReady(true);
      setPaypalScriptError(null);
      return;
    }

    setPaypalReady(false);
    setPaypalScriptError(null);

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${paypalConfig.client_id}&currency=${(paypalConfig.currency || 'USD').toUpperCase()}`;
    script.async = true;
    script.onload = () => {
      setPaypalReady(true);
    };
    script.onerror = () => {
      setPaypalScriptError('Failed to load PayPal SDK');
      message.error('Failed to load PayPal SDK');
    };
    document.body.appendChild(script);

    return () => {
      script.remove();
      setPaypalReady(false);
    };
  }, [paypalConfig]);

  useEffect(() => {
    if (!paypalReady || !paypalButtonsRef.current || !window.paypal || !topUpModalVisible || topUpStage !== 'confirm') {
      return;
    }

    if (!topUpAmount) {
      return;
    }

    paypalButtonsRef.current.innerHTML = '';

    const buttons = window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'rect',
      },
      createOrder: async () => {
        const amountValue = Number(topUpAmount);
        if (topUpAmount == null || Number.isNaN(amountValue) || amountValue <= 0) {
          message.warning('Please enter an amount greater than 0');
          throw new Error('Invalid amount');
        }

        const currencyCode = (wallet?.currency || paypalConfig?.currency || 'USD').toUpperCase();
        const formattedAmount = amountValue.toFixed(2);

        try {
          const payload = {
            amount: formattedAmount,
            currency: currencyCode,
          };

          // Include order items if paying for orders
          if (paymentContext?.orders && paymentContext.orders.length > 0) {
            payload.order_items = paymentContext.orders.map(order => ({
              name: order.product_name,
              sku: order.order_number
            }));
          }

          const response = await paypalAPI.createOrder(payload);
          return response.data.id;
        } catch (error) {
          
          message.error('Unable to initiate PayPal payment');
          throw error;
        }
      },
      onApprove: async (data) => {
        try {
          setPaypalProcessing(true);
          await paypalAPI.captureOrder(data.orderID);
          message.success('Payment successful');

          // Refresh wallet data after a short delay
          setTimeout(() => {
            fetchWalletSummary();
            fetchTransactions(1, pagination.pageSize);
          }, 2000);
        } catch (error) {
          
          message.error('PayPal payment confirmation failed');
        } finally {
          // Close the modal
          closeTopUpModal();
          setPaypalProcessing(false);
        }
      },
      onCancel: () => {
        setPaypalProcessing(false);
      },
      onError: (error) => {
        
        message.error('PayPal payment encountered an error');
        setPaypalProcessing(false);
      },
    });

    buttons.render(paypalButtonsRef.current);

    return () => {
      buttons.close();
    };
  }, [paypalReady, topUpAmount, paypalConfig, wallet?.currency, fetchWalletSummary, fetchTransactions, pagination.pageSize, topUpModalVisible, topUpStage]);

  useEffect(() => {
    if (topUpModalVisible && topUpStage === 'confirm') {
      return;
    }
    if (paypalButtonsRef.current) {
      paypalButtonsRef.current.innerHTML = '';
    }
  }, [topUpModalVisible, topUpStage]);

  const handleTableChange = (config) => {
    const { current, pageSize } = config;
    fetchTransactions(current, pageSize);
  };

  const openDetails = (record) => {
    setSelectedTransaction(record);
    setDetailModalVisible(true);
  };

  const resetTopUpFlow = () => {
    setTopUpAmount(null);
    setDraftTopUpAmount(null);
    setTopUpStage('input');
    setPaypalProcessing(false);
    setPaymentContext(null);
  };

  const openTopUpModal = () => {
    resetTopUpFlow();
    setTopUpModalVisible(true);
  };

  const closeTopUpModal = () => {
    setTopUpModalVisible(false);
    resetTopUpFlow();
  };

  const handleConfirmTopUpAmount = (amountOverride) => {
    const rawAmount = amountOverride ?? draftTopUpAmount;
    const amountValue = Number(rawAmount);
    if (rawAmount == null || Number.isNaN(amountValue) || amountValue <= 0) {
      message.warning('Please enter an amount greater than 0');
      return;
    }
    setTopUpAmount(amountValue);
    setDraftTopUpAmount(amountValue);
    setTopUpStage('confirm');
  };

  const handleStripePayment = async () => {
    const amountValue = Number(topUpAmount);
    if (topUpAmount == null || Number.isNaN(amountValue) || amountValue <= 0) {
      message.warning('Please enter an amount greater than 0');
      return;
    }

    const currencyCode = (wallet?.currency || 'USD').toUpperCase();
    const formattedAmount = amountValue.toFixed(2);

    try {
      setStripeProcessing(true);
      const payload = {
        amount: formattedAmount,
        currency: currencyCode,
      };

      // Include order information if paying for orders
      if (paymentContext?.orders && paymentContext.orders.length > 0) {
        payload.order_items = paymentContext.orders.map(order => ({
          name: order.product_name,
          sku: order.order_number
        }));
      }

      const response = await stripeAPI.createCheckoutSession(payload);

      // Open Stripe Checkout in a new window
      const checkoutWindow = window.open(response.data.url, '_blank');
      if (!checkoutWindow) {
        message.error('Please allow pop-ups to complete payment');
        return;
      }

      // Close the top-up modal
      closeTopUpModal();

      // Show a message that payment is in progress
      message.info('Payment window opened. Complete the payment in the new window.');

      // Optionally poll for payment completion or rely on webhook
      // For now, we'll just show a success message when the page regains focus
      const handleFocus = () => {
        // Refresh wallet data when user comes back
        fetchWalletSummary();
        fetchTransactions(1, pagination.pageSize);
        window.removeEventListener('focus', handleFocus);
      };
      window.addEventListener('focus', handleFocus);

    } catch (error) {
      
      message.error('Unable to initiate Stripe payment');
    } finally {
      setStripeProcessing(false);
    }
  };

  const renderAmount = (record) => {
    const amount = normalizeMoney(record.amount_minor || '0');
    const currency = record.currency || 'USD';
    if (record.direction === 'debit') {
      return (
        <Text type="danger">
          -{currency} {amount}
        </Text>
      );
    }
    return (
      <Text type="success">
        +{currency} {amount}
      </Text>
    );
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
      width: 180,
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      render: (value) => (
        <Tag color={value === 'credit' ? 'green' : 'volcano'}>
          {value === 'credit' ? 'Credit' : 'Debit'}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Amount',
      key: 'amount_minor',
      render: (_, record) => renderAmount(record),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => reasonLabelMap[value] || value,
    },
    {
      title: 'Order / Remarks',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (_, record) => {
        // Only show order info for purchase transactions
        if (record.reason === 'purchase') {
          const orderRef = normalizeOrderRef(record.order_id);
          if (!orderRef) return '—';
          const count = orderItemCounts[orderRef];
          const countLabel = count == null ? 'N/A' : `${count} item${count === 1 ? '' : 's'}`;
          return (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate(`/dropshipper/orders/${orderRef}`)}
            >
              #{orderRef} · {countLabel}
            </Button>
          );
        }
        // For other reasons, show the comment
        return record.comment || '—';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => openDetails(record)} icon={<EyeOutlined />}>View Details</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size={24}>
        <div>
          <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>My Wallet</Title>
          </Space>
          <Row gutter={16} align="middle">
            <Col xs={24} lg={12}>
              <Card loading={summaryLoading}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <Statistic
                    title="Balance"
                    prefix={`${wallet?.currency || 'USD'} `}
                    value={wallet ? parseFloat(normalizeMoney(wallet.balance_minor || '0')) : 0}
                    precision={2}
                    valueStyle={{ color: '#3f8600' }}
                  />
                  <Button
                    type="primary"
                    size="large"
                    onClick={openTopUpModal}
                    disabled={!paypalConfig}
                    style={{ minWidth: 140 }}
                  >
                    Top Up
                  </Button>
                </div>
                {!paypalConfig && (
                  <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                    Loading PayPal configuration…
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card loading={summaryLoading}>
                <Statistic
                  title="Pending"
                  prefix={`${wallet?.currency || 'USD'} `}
                  value={wallet ? parseFloat(normalizeMoney(wallet.pending_minor || '0')) : 0}
                  precision={2}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>
        </div>

        <Card>
          <Table
            rowKey="id"
            loading={txLoading}
            columns={columns}
            dataSource={transactions}
            pagination={pagination}
            onChange={handleTableChange}
          />
        </Card>
      </Space>

      <Modal
        title="Payment"
        open={topUpModalVisible}
        onCancel={closeTopUpModal}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {topUpStage === 'input' ? (
            <>
              <InputNumber
                value={draftTopUpAmount}
                onChange={(value) => setDraftTopUpAmount(value)}
                min={0.01}
                precision={2}
                style={{ width: '100%' }}
                addonAfter={wallet?.currency || paypalConfig?.currency || 'USD'}
                placeholder="Enter amount"
                disabled={!paypalConfig || paypalProcessing}
              />
              <Text strong style={{ display: 'block' }}>Quick Select</Text>
              <Space wrap>
                {quickTopUpAmounts.map((amount) => (
                  <Button
                    key={amount}
                    onClick={() => handleConfirmTopUpAmount(amount)}
                    disabled={!paypalConfig || paypalProcessing}
                  >
                    {amount} {(wallet?.currency || paypalConfig?.currency || 'USD').toUpperCase()}
                  </Button>
                ))}
              </Space>
              {!paypalConfig && (
                <Text type="secondary">Loading PayPal configuration…</Text>
              )}
              <Button
                type="primary"
                size="large"
                block
                onClick={() => handleConfirmTopUpAmount()}
                disabled={!paypalConfig}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              {paymentContext?.orders && paymentContext.orders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Paying for orders:</Text>
                  {paymentContext.orders.length === 1 ? (
                    <div style={{ marginTop: 8 }}>
                      <Text>{paymentContext.orders[0].product_name}</Text>
                      <br />
                      <Text type="secondary">Order #{paymentContext.orders[0].order_number}</Text>
                    </div>
                  ) : (
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                    >
                      <Panel header={`${paymentContext.orders.length} orders`} key="orders">
                        {paymentContext.orders.map((order, index) => (
                          <div key={index} style={{ marginBottom: 8 }}>
                            <Text>{order.product_name}</Text>
                            <br />
                            <Text type="secondary">Order #{order.order_number}</Text>
                          </div>
                        ))}
                      </Panel>
                    </Collapse>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Your balance: ${wallet ? parseFloat(normalizeMoney(wallet.balance_minor || '0')).toFixed(2) : '0.00'}, left to pay:
                    </Text>
                  </div>
                </div>
              )}
              <Title level={4} style={{ margin: 0 }}>
                {(wallet?.currency || paypalConfig?.currency || 'USD').toUpperCase()} {topUpAmount ? Number(topUpAmount).toFixed(2) : '0.00'}
              </Title>
              {paypalScriptError && (
                <Text type="danger">{paypalScriptError}</Text>
              )}
              {paypalConfig && !paypalReady && !paypalScriptError && (
                <Text type="secondary">Loading PayPal buttons…</Text>
              )}
              <div ref={paypalButtonsRef} />
              {paypalProcessing && (
                <Text type="secondary">Processing PayPal payment…</Text>
              )}
              {stripeEnabled && (
                <>
                  <Button
                    type="default"
                    size="large"
                    block
                    onClick={handleStripePayment}
                    disabled={stripeProcessing || paypalProcessing}
                    loading={stripeProcessing}
                    style={{
                      backgroundColor: '#635bff',
                      borderColor: '#635bff',
                      color: 'white',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => {
                      if (!stripeProcessing && !paypalProcessing) {
                        e.currentTarget.style.backgroundColor = '#5046e5';
                        e.currentTarget.style.borderColor = '#5046e5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!stripeProcessing && !paypalProcessing) {
                        e.currentTarget.style.backgroundColor = '#635bff';
                        e.currentTarget.style.borderColor = '#635bff';
                      }
                    }}
                  >
                    Pay with Stripe
                  </Button>
                </>
              )}
              <Space style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="default"
                  onClick={closeTopUpModal}
                  disabled={paypalProcessing || stripeProcessing}
                >
                  Cancel
                </Button>
              </Space>
              {paypalConfig?.environment && paypalConfig.environment !== 'PRODUCTION' && (
                <Tag color="gold">{`PayPal ${paypalConfig.environment} mode`}</Tag>
              )}
            </>
          )}
        </Space>
      </Modal>

      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedTransaction && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Transaction ID">{selectedTransaction.id}</Descriptions.Item>
            <Descriptions.Item label="Created At">
              {selectedTransaction.created_at ? dayjs(selectedTransaction.created_at).format('YYYY-MM-DD HH:mm:ss') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">{selectedTransaction.status}</Descriptions.Item>
            <Descriptions.Item label="Direction">{selectedTransaction.direction}</Descriptions.Item>
            <Descriptions.Item label="Amount">{`${selectedTransaction.currency || 'USD'} ${normalizeMoney(selectedTransaction.amount_minor || '0')}`}</Descriptions.Item>
            <Descriptions.Item label="Reason">{reasonLabelMap[selectedTransaction.reason] || selectedTransaction.reason}</Descriptions.Item>
            <Descriptions.Item label="Running Balance">
              {selectedTransaction.running_balance_minor == null
                ? '—'
                : `${selectedTransaction.currency || 'USD'} ${normalizeMoney(selectedTransaction.running_balance_minor)}`}
            </Descriptions.Item>
            <Descriptions.Item label="Order">
              {(() => {
                const orderRef = normalizeOrderRef(selectedTransaction.order_id);
                if (!orderRef) return '—';
                const count = orderItemCounts[orderRef];
                const countLabel = count == null ? 'N/A' : `${count} item${count === 1 ? '' : 's'}`;
                return (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/dropshipper/orders/${orderRef}`)}
                  >
                    #{orderRef} · {countLabel}
                  </Button>
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="External Reference">{selectedTransaction.external_ref || '—'}</Descriptions.Item>
            <Descriptions.Item label="Idempotency Key">{selectedTransaction.idempotency_key || '—'}</Descriptions.Item>
            <Descriptions.Item label="Metadata">
              {selectedTransaction.meta ? <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(selectedTransaction.meta, null, 2)}</pre> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes">{selectedTransaction.comment || '—'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MyWallet;
